import type { CampaignCreationCapabilities, SponsoredBrand } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DisabledSelectField, requiredLabel, SelectField, TextField } from "./FormFields";
import { currencyLabel, moneyStep } from "./sponsored-brands-format";

interface CampaignDetailsFieldsProps {
  capabilities: Pick<CampaignCreationCapabilities, "currencyCode" | "dailyBudget">;
  campaignName: string;
  onCampaignNameChange: (value: string) => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  budget: string;
  onBudgetChange: (value: string) => void;
  brandId: string | null;
  onBrandChange: (value: string | null) => void;
  brands: SponsoredBrand[];
  discoveryLoading: boolean;
  discoveryFailed: boolean;
  errors: Partial<Record<"name" | "startDate" | "dailyBudget" | "brandId", string>>;
}

export function CampaignDetailsFields({
  capabilities,
  campaignName,
  onCampaignNameChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  budget,
  onBudgetChange,
  brandId,
  onBrandChange,
  brands,
  discoveryLoading,
  discoveryFailed,
  errors,
}: CampaignDetailsFieldsProps) {
  const { t } = useTranslation("campaignCreation");
  return (
    <FieldGroup data-slot="sb-campaign-fields" className="grid grid-cols-2 gap-x-6 gap-y-5">
      <TextField
        name="campaignName"
        label={t("sb.campaign.name")}
        value={campaignName}
        onChange={onCampaignNameChange}
        error={errors.name}
        required
      />
      <DisabledSelectField
        label={t("sb.campaign.portfolios")}
        blocker={t("sb.blockers.portfolioContract")}
      />
      <FieldGroup data-slot="sb-campaign-date-fields" className="grid grid-cols-2 gap-2">
        <TextField
          name="startDate"
          label={t("sb.campaign.startDate")}
          type="date"
          value={startDate}
          onChange={onStartDateChange}
          error={errors.startDate}
          required
        />
        <TextField
          name="endDate"
          label={t("sb.campaign.endDate")}
          type="date"
          value={endDate}
          onChange={onEndDateChange}
        />
      </FieldGroup>
      <Field data-invalid={errors.dailyBudget ? true : undefined}>
        <FieldLabel>{requiredLabel(t("sb.campaign.budget"))}</FieldLabel>
        <div className="flex w-full gap-2">
          <InputGroup className="flex-1">
            <InputGroupAddon>
              <InputGroupText>{currencyLabel(capabilities.currencyCode)}</InputGroupText>
            </InputGroupAddon>
            <InputGroupInput
              name="budget"
              type="number"
              min={capabilities.dailyBudget.min}
              max={capabilities.dailyBudget.max}
              step={moneyStep(capabilities.dailyBudget.decimals)}
              value={budget}
              aria-invalid={errors.dailyBudget ? true : undefined}
              onChange={(event) => onBudgetChange(event.target.value)}
            />
          </InputGroup>
          <Select items={[{ value: "daily", label: t("sb.campaign.daily") }]} value="daily">
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="daily">{t("sb.campaign.daily")}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {errors.dailyBudget ? <FieldError>{errors.dailyBudget}</FieldError> : null}
      </Field>
      <SelectField
        label={t("sb.campaign.brand")}
        value={brandId}
        onValueChange={onBrandChange}
        options={brands.map((brand) => ({ value: brand.brandId, label: brand.name }))}
        disabled={discoveryLoading || discoveryFailed}
        description={discoveryFailed ? t("sb.blockers.brandData") : undefined}
        error={errors.brandId}
        required
      />
    </FieldGroup>
  );
}
